/*  Shell condivisa per le sotto-pagine della Proposta C.
    Sidebar glass + topbar + selettore prototipo.
*/

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PROPOSALS } from './_mobileShared';
import {
  LayoutGrid, Ticket, ListChecks, BarChart3, Zap, Building2, Settings,
  Search, Bell, Plus, Activity
} from 'lucide-react';

export const C = PROPOSALS.c;

const NAV = [
  { icon: LayoutGrid, label: 'Overview', to: '/mockup/v2/c' },
  { icon: Ticket, label: 'Tickets', to: '/mockup/v2/c/tickets' },
  { icon: ListChecks, label: 'Programmate', to: '/mockup/v2/c/scheduled' },
  { icon: Zap, label: 'Energy', to: '/mockup/v2/c/consumption' },
  { icon: BarChart3, label: 'Analytics' },
  { icon: Building2, label: 'Hotels' },
  { icon: Settings, label: 'System' },
];

const PROTO_TABS = [
  { id: 'overview', label: 'Dashboard', to: '/mockup/v2/c' },
  { id: 'tickets', label: 'Tickets', to: '/mockup/v2/c/tickets' },
  { id: 'scheduled', label: 'Programmate', to: '/mockup/v2/c/scheduled' },
  { id: 'consumption', label: 'Consumi', to: '/mockup/v2/c/consumption' },
];

export const CShell = ({ children, hotel = 'Hotel Galleria' }) => {
  const loc = useLocation();
  return (
    <div
      className="min-h-screen flex"
      style={{ background: C.bg, fontFamily: C.fontBody, color: C.text }}
      data-testid="proposal-c-shell"
    >
      {/* Sidebar */}
      <aside
        className="w-16 border-r flex flex-col py-4 sticky top-0 h-screen"
        style={{
          background: 'rgba(18,18,20,0.6)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderColor: C.border,
        }}
      >
        <div className="px-3 mb-5">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold"
            style={{
              background: `linear-gradient(135deg, ${C.accent}, #1d4ed8)`,
              fontFamily: C.fontDisplay,
              boxShadow: `0 0 24px ${C.accentGlow}`,
            }}
          >
            HF
          </div>
        </div>
        <nav className="flex-1 space-y-1.5 px-3">
          {NAV.map((n, i) => {
            const active = n.to && (loc.pathname === n.to);
            const Wrapper = n.to ? Link : 'div';
            return (
              <Wrapper
                key={i}
                to={n.to || '#'}
                className="w-10 h-10 rounded-lg flex items-center justify-center relative"
                style={{
                  background: active ? C.surface2 : 'transparent',
                  color: active ? C.text : C.mutedDim,
                  border: active ? `1px solid ${C.border}` : '1px solid transparent',
                }}
              >
                <n.icon className="w-4 h-4" />
                {active && (
                  <span
                    className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r"
                    style={{ background: C.accent, boxShadow: `0 0 12px ${C.accent}` }}
                  />
                )}
              </Wrapper>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 border-b flex items-center px-6 gap-4 sticky top-0 z-10" style={{ borderColor: C.border, background: C.bg }}>
          <div className="flex items-center gap-2 text-xs" style={{ fontFamily: C.fontMono, color: C.muted }}>
            <Activity className="w-3.5 h-3.5" style={{ color: C.success }} />
            <span>SYSTEM</span>
            <span style={{ color: C.success }}>● LIVE</span>
            <span style={{ color: C.mutedDim }}>·</span>
            <span>{hotel.toUpperCase()}</span>
          </div>
          <div className="flex-1 max-w-md">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-md"
              style={{ background: C.surface, border: `1px solid ${C.border}` }}
            >
              <Search className="w-3.5 h-3.5" style={{ color: C.mutedDim }} />
              <input
                placeholder="$ search tickets, assets, rooms..."
                className="flex-1 bg-transparent text-xs outline-none"
                style={{ fontFamily: C.fontMono, color: C.text }}
              />
              <kbd
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: C.surface2, color: C.muted, fontFamily: C.fontMono }}
              >
                ⌘K
              </kbd>
            </div>
          </div>
          <button className="relative p-2 rounded-md" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
            <Bell className="w-3.5 h-3.5" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: C.danger, boxShadow: `0 0 8px ${C.danger}` }} />
          </button>
          <button
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{
              background: C.accent,
              color: '#fff',
              fontFamily: C.fontMono,
              boxShadow: `0 0 24px ${C.accentGlow}`,
            }}
          >
            <Plus className="w-3.5 h-3.5" /> NEW
          </button>
          <Link to="/mockup/v2" className="text-xs hover:text-white" style={{ color: C.muted, fontFamily: C.fontMono }}>
            ← COMPARE
          </Link>
        </header>

        {/* Prototype sub-nav */}
        <div className="px-6 h-10 flex items-center gap-1 border-b" style={{ borderColor: C.border, background: C.bg }}>
          <span className="text-[10px] tracking-widest mr-3" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            PROTOTYPE_C:
          </span>
          {PROTO_TABS.map((tab) => {
            const active = loc.pathname === tab.to;
            return (
              <Link
                key={tab.id}
                to={tab.to}
                className="px-3 py-1 rounded text-xs"
                style={{
                  background: active ? C.surface2 : 'transparent',
                  border: active ? `1px solid ${C.border}` : '1px solid transparent',
                  color: active ? C.text : C.muted,
                  fontFamily: C.fontMono,
                }}
                data-testid={`proto-tab-${tab.id}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};

/* Componenti riusabili stile C */

export const CBadge = ({ children, color = C.muted, bg }) => (
  <span
    className="text-[10px] px-2 py-0.5 rounded font-semibold tracking-wider"
    style={{
      background: bg || `${color}22`,
      color,
      fontFamily: C.fontMono,
    }}
  >
    {children}
  </span>
);

export const CCard = ({ children, className = '', padding = 'p-5', label, title }) => (
  <section
    className={`rounded-lg ${padding} ${className}`}
    style={{ background: C.surface, border: `1px solid ${C.border}` }}
  >
    {(label || title) && (
      <div className="mb-4">
        {label && (
          <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            {label}
          </div>
        )}
        {title && (
          <h2 className="text-sm font-semibold mt-0.5" style={{ fontFamily: C.fontDisplay }}>
            {title}
          </h2>
        )}
      </div>
    )}
    {children}
  </section>
);
